import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Textarea } from '@/components/common/Textarea';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ImageInput from './ImageInput';
import { FileUpload } from '@/types/general';
import { FORUM, ROUTES, SEO_ENTITY_ID, SEO_ENTITY_TYPE } from '@/configs/constants';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import { useStore } from '@/hooks/useStore';
import { flowResult, toJS } from 'mobx';
import { toastify } from '@/utils/toastify';
import React, { useEffect, useMemo, useState } from 'react';
import { GetSeoResponse } from '@/types/http-payload/forum';
import { useNavigate, useParams } from 'react-router-dom';
import { extractImageSrcAndAltList, extractTagretAndRelList, uploadFileToS3UsingPresignedPost } from '@/utils/utils';
import { FORUM_TYPE, INFO_TYPE } from '@/types/enums';
import { ScrollArea, ScrollBar } from '@/components/common/ScrollArea';
import { Image } from '@/components/common/Image';
import { useRef } from 'react';
import { MultiSelect } from '@/components/common/MultiSelect';

const ROBOT_CHECKBOX = {
    INDEX: [
        {
            key: 'index',
            value: 'Index'
        },
        {
            key: 'noindex',
            value: 'NoIndex'
        }
    ],
    FOLLOW: [
        {
            key: 'follow',
            value: 'Follow'
        },
        {
            key: 'nofollow',
            value: 'NoFollow'
        }
    ]
};

interface SeoEntity {
    seoEntityType: string
}
interface ImgSEO {
    src: string,
    alt?: string
}

interface LinkSEO {
    href: string,
    rel?: string,
    target?: string,
    text?: string

}

export default observer(function SeoSetting({ seoEntityType }: SeoEntity) {
    type FormData = yup.InferType<typeof validateSchema>;
    const { t } = useTranslation();
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const SEO_LINK_ATTRIBUTES = [
        { key: 1, label: t('select_options.open_link_in_new_tab'), value: '_blank', type: 'target' },
        { key: 2, label: t('select_options.add_ref_nofollow'), value: 'nofollow', type: 'rel' },
        { key: 3, label: t('select_options.add_ref_sponsored'), value: 'sponsored', type: 'rel' }
    ];

    const { code } = useParams();
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const validateSchema = useMemo(() => {
        return yup.object().shape({
            entityScreenType: yup.string().required(),
            metaTitle: yup.string(),
            metaDescription: yup.string(),
            robotIndex: yup.string(),
            robotFollow: yup.string(),
            OGTitle: yup.string(),
            OGDescription: yup.string(),
            OGimage: yup.mixed<string | File>(),
            OgImageUrl: yup.string(),

            slug: yup.string()
                .nullable()
                .transform(value => (value === '' ? null : value))
                .when([], {
                    is: () => seoEntityType !== SEO_ENTITY_TYPE.STATIC,
                    then: schema => schema
                        .required('seo_settings.slug_input_error')
                        .matches(/^[a-z0-9-]+$/, 'seo_settings.slug_input_error'),
                    otherwise: schema => schema.notRequired().nullable('')
                }),

            canonicalUrl: yup.string()
                .nullable()
                .transform(value => (value === '' ? null : value))
                .when([], {
                    is: () => seoEntityType !== SEO_ENTITY_TYPE.STATIC,
                    then: schema => schema
                        .matches(/^[a-z0-9-]+$/, {
                            message: 'seo_settings.canonical_input_error',
                            excludeEmptyString: true
                        }),
                    otherwise: schema => schema.notRequired().nullable('')
                }),
            listImgSEO: yup.array().of(
                yup.object().shape({
                    src: yup.string(),
                    alt: yup.string()
                })
            ),
            listLinkSEO: yup.array().of(
                yup.object().shape({
                    href: yup.string(),
                    rel: yup.string(),
                    target: yup.string(),
                    text: yup.string()
                })
            )

        });
    }, [seoEntityType]);

    const {
        generalSettingStore: { getSeoCms, updateSeoCms, getPresignedUrl },
        shopStore: { shopInfo, getShopInfo },
        topicStore: { topicDetail, getTopicDetail },
        newsStore: { newsDetail, getNewsDetail }
    } = useStore();

    const refetchData = async () => {
        if (seoEntityType === SEO_ENTITY_TYPE.SHOP) {
            await flowResult(getShopInfo(code ?? '', { typeInfo: INFO_TYPE.ShopInfo }));
        } else if (seoEntityType === SEO_ENTITY_TYPE.TOPIC) {
            await flowResult(getTopicDetail(code ?? ''));
        } else if (seoEntityType === SEO_ENTITY_TYPE.NEWS) {
            await flowResult(getNewsDetail(code ?? ''));
        }
    };

    const navigate = useNavigate();

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema, {
            context: {
                isSlugVisible: seoEntityType !== SEO_ENTITY_TYPE.STATIC
            }
        }),
        mode: 'onChange',
        defaultValues: {
            entityScreenType: SEO_ENTITY_ID[0].key,
            metaTitle: '',
            metaDescription: '',
            robotIndex: 'index',
            robotFollow: 'follow',
            OGTitle: '',
            OGDescription: '',
            OGimage: '',
            slug: '',
            canonicalUrl: '',
            listImgSEO: [],
            listLinkSEO: []
        }
    });

    const entityScreenTypeWatch = form.watch('entityScreenType');

    // initial data fetch
    useEffect(() => {
        changeDataByEntityType(entityScreenTypeWatch);
    }, [entityScreenTypeWatch]);

    const setSeoFormValues = (seoData: any) => {
        form.setValue('metaTitle', seoData?.metaTag?.title || '');
        form.setValue('metaDescription', seoData?.metaTag?.description || '');
        form.setValue(
            'robotIndex',
            seoData?.robots?.isIndex === false ? ROBOT_CHECKBOX.INDEX[1].key : ROBOT_CHECKBOX.INDEX[0].key
        );
        form.setValue(
            'robotFollow',
            seoData?.robots?.isFollow === false ? ROBOT_CHECKBOX.FOLLOW[1].key : ROBOT_CHECKBOX.FOLLOW[0].key
        );
        form.setValue('OGTitle', seoData?.openGraph?.title || '');
        form.setValue('OGDescription', seoData?.openGraph?.description || '');
        form.setValue('OGimage', seoData?.openGraph?.image || '');
        form.setValue('OgImageUrl', seoData?.openGraph?.imageUrl || '');
        if (seoEntityType !== SEO_ENTITY_TYPE.STATIC) {
            let slug = '';
            if (seoEntityType === SEO_ENTITY_TYPE.SHOP) slug = shopInfo?.slug || '';
            else if (seoEntityType === SEO_ENTITY_TYPE.TOPIC) slug = topicDetail?.slug || '';
            else if (seoEntityType === SEO_ENTITY_TYPE.NEWS) slug = newsDetail?.slug || '';

            form.setValue(
                'slug',
                slug
            );
            form.setValue('canonicalUrl', seoData?.canonicalUrl || '');
        }
    };

    const changeDataByEntityType = async (entityScreenTypeWatch: string) => {
        let type = '';
        let entityId = '';

        if (seoEntityType === SEO_ENTITY_TYPE.STATIC) {
            type = SEO_ENTITY_TYPE.STATIC;
            entityId = entityScreenTypeWatch;
        } else if (seoEntityType === SEO_ENTITY_TYPE.SHOP && shopInfo?.slug) {
            type = SEO_ENTITY_TYPE.SHOP;
            entityId = shopInfo.slug;
        } else if (seoEntityType === SEO_ENTITY_TYPE.TOPIC && topicDetail?.slug) {
            type = SEO_ENTITY_TYPE.TOPIC;
            entityId = topicDetail.slug;
        } else if (seoEntityType === SEO_ENTITY_TYPE.NEWS && newsDetail?.slug) {
            type = SEO_ENTITY_TYPE.NEWS;
            entityId = newsDetail.slug;
        }

        if (type && entityId) {
            const res = await flowResult(getSeoCms(type, entityId));
            if (res && res.ok) {
                setPreviewImage(null);
                setSeoFormValues(res.data.seoData);
            }
        }
    };

    const buildSeoPayload = ({
        metaTitle,
        metaDescription,
        robotIndex,
        robotFollow,
        OgDescription,
        OgTitle,
        OgImage,
        canonicalUrl,
        listImgSEO,
        listLinkSEO
    }: {
        metaTitle?: string,
        metaDescription?: string,
        robotIndex?: string,
        robotFollow?: string,
        OgDescription?: string,
        OgTitle?: string,
        OgImage?: string,
        canonicalUrl?: string,
        listImgSEO?: ImgSEO[],
        listLinkSEO?: LinkSEO[]
    }) => ({
        seoData: {
            meta_tag: {
                title: metaTitle || '',
                description: metaDescription || ''
            },
            robots: {
                isIndex: robotIndex === ROBOT_CHECKBOX.INDEX[0].key || false,
                isFollow: robotFollow === ROBOT_CHECKBOX.FOLLOW[0].key || false
            },
            open_graph: {
                title: OgTitle || '',
                description: OgDescription || '',
                image: OgImage || ''
            },
            canonicalUrl: canonicalUrl || ''
        },
        listImgSEO: listImgSEO?.map((item, index) => {
            return {
                src: item.src,
                alt: item.alt
            };
        }),
        listLinkSEO: listLinkSEO?.map((item, index) => {
            return {
                href: item.href,
                rel: item.rel,
                target: item.target
            };
        })

    });

    const onSubmit = async (data: FormData) => {
        const {
            entityScreenType,
            metaTitle,
            metaDescription,
            robotIndex,
            robotFollow,
            OGimage,
            OGDescription,
            OGTitle,
            slug,
            canonicalUrl,
            listImgSEO,
            listLinkSEO
        } = data;

        if (typeof OGimage === 'object' && OGimage instanceof File) {
            const res: any = await flowResult(getPresignedUrl([{ name: OGimage.name, type: OGimage.type }]));
            await uploadFileToS3UsingPresignedPost(OGimage, res.presignedUrls[0]);
            data.OGimage = res.presignedUrls[0].fields.key;
        } else if (typeof OGimage === 'string' && OGimage.length > 0) {
            data.OGimage = OGimage;
        } else {
            data.OGimage = '';
        }
        let type = '';
        let entityId = '';

        if (seoEntityType === SEO_ENTITY_TYPE.STATIC) {
            type = SEO_ENTITY_TYPE.STATIC;
            entityId = entityScreenType;
        } else if (seoEntityType === SEO_ENTITY_TYPE.SHOP && shopInfo?.slug) {
            type = SEO_ENTITY_TYPE.SHOP;
            entityId = shopInfo.slug;
        } else if (seoEntityType === SEO_ENTITY_TYPE.TOPIC && topicDetail?.slug) {
            type = SEO_ENTITY_TYPE.TOPIC;
            entityId = topicDetail.slug;
        } else if (seoEntityType === SEO_ENTITY_TYPE.NEWS && newsDetail?.slug) {
            type = SEO_ENTITY_TYPE.NEWS;
            entityId = newsDetail.slug;
        }

        if (type && entityId) {
            const payload = buildSeoPayload({
                metaTitle,
                metaDescription,
                robotIndex,
                robotFollow,
                OgDescription: OGDescription,
                OgTitle: OGTitle,
                OgImage: data.OGimage as string,
                canonicalUrl: canonicalUrl as string,
                listImgSEO: listImgSEO as ImgSEO[],
                listLinkSEO: listLinkSEO as LinkSEO[]

            });
            const res = await flowResult(updateSeoCms(type, entityId, { ...payload, slug: slug || '' }));
            if (res && res.ok) {
                toastify('success', t('messages.update_success'));
                refetchData();
            }
        }
    };

    // hooks
    // store

    const back = () => {
        if (seoEntityType === SEO_ENTITY_TYPE.STATIC) {
        } else if (seoEntityType === SEO_ENTITY_TYPE.SHOP) {
            navigate(ROUTES.shopManagement.href);
        } else if (seoEntityType === SEO_ENTITY_TYPE.TOPIC) {
            navigate(ROUTES.forum.href);
        } else if (seoEntityType === SEO_ENTITY_TYPE.NEWS) {
            navigate(ROUTES.news.href);
        }
    };

    const getPrefixUrl = () => {
        if (seoEntityType === SEO_ENTITY_TYPE.SHOP) {
            return `${process.env.REACT_APP_EU_URL}/shops/`;
        } else if (seoEntityType === SEO_ENTITY_TYPE.TOPIC) {
            return `${process.env.REACT_APP_EU_URL}/forum/${topicDetail?.forum === FORUM_TYPE.Career ? 'miapet-career/' : 'free-topic/'}`;
        } else if (seoEntityType === SEO_ENTITY_TYPE.NEWS) {
            return `${process.env.REACT_APP_EU_URL}/news/`;
        }
    };

    useEffect(() => {
        if (topicDetail?.description && SEO_ENTITY_TYPE.TOPIC === seoEntityType) {
            const listImg = extractImageSrcAndAltList(topicDetail?.description || '');
            const listLink = extractTagretAndRelList(topicDetail?.description || '');
            form.setValue('listImgSEO', listImg);
            form.setValue('listLinkSEO', listLink);
        } else if (newsDetail?.description && SEO_ENTITY_TYPE.NEWS === seoEntityType) {
            const listImg = extractImageSrcAndAltList(newsDetail?.description || '');
            const listLink = extractTagretAndRelList(newsDetail?.description || '');
            form.setValue('listImgSEO', listImg);
            form.setValue('listLinkSEO', listLink);
        }
    }, [topicDetail?.description, newsDetail?.description]);

    return (
        <div className='rounded h-full mt-4'>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='w-full'>
                    {/* Shop Tags */}
                    {seoEntityType === SEO_ENTITY_TYPE.STATIC && (
                        <div className='bg-white py-[10px] px-[16px] rounded mb-2'>
                            <div className='flex md:flex-row flex-col md:gap-4 gap-0'>
                                <FormField
                                    control={form.control}
                                    name='entityScreenType'
                                    render={({ field }) => (
                                        <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                            <FormControl>
                                                <Select key={field.value} onValueChange={field.onChange} {...field}>
                                                    <SelectTrigger className='mt-1.5'>
                                                        <SelectValue
                                                            placeholder={t('placeholder.choose')}
                                                        >
                                                        </SelectValue>
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {SEO_ENTITY_ID.map((item, index) => (
                                                            <SelectItem
                                                                className='hover:cursor-pointer'
                                                                key={index}
                                                                value={item.key}
                                                            >
                                                                <span>{item.label}</span>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                    )}

                    {/* Meta Tags */}
                    <div className='bg-white py-[10px] px-[16px] rounded'>
                        <div className='text-lg font-bold text-gray-800 mb-4 border-b border-b-[#E8E8E8] pb-2'>
                            {t('words_title.meta_box_header')}
                        </div>
                        <div className='mb-4'>
                            <Label className='block text-sm font-medium text-gray-600 mb-1'>
                                {t('words_title.meta_box_title')}
                            </Label>
                            <FormField
                                control={form.control}
                                name='metaTitle'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input type='text' {...field} value={field.value ?? ''} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className=''>
                            <Label className='block text-sm font-medium text-gray-600 mb-1'>
                                {t('words_title.meta_box_description')}
                            </Label>
                            <FormField
                                control={form.control}
                                name='metaDescription'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Textarea
                                                maxLength={1000}
                                                className='text-input-1 min-h-[200px]'
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='mt-4'>
                            <Label className='block text-sm font-medium text-gray-600 mb-2'>
                                {t('words_title.meta_box_robots')}
                            </Label>
                            <div className='flex flex-wrap gap-6 '>
                                <div className='flex gap-4 items-center border border-[#F2F2F2] px-6 py-3 rounded-sm'>
                                    <FormField
                                        control={form.control}
                                        name='robotIndex'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl className=''>
                                                    <RadioGroup
                                                        key={field.value}
                                                        className='flex items-center gap-24'
                                                        onValueChange={(value) => {
                                                            field.onChange(value);
                                                        }}
                                                        value={field.value}
                                                    >
                                                        {ROBOT_CHECKBOX.INDEX.map((item, index) => (
                                                            <FormItem key={index} className='flex items-center gap-2'>
                                                                <FormControl>
                                                                    <RadioGroupItem value={item.key} />
                                                                </FormControl>
                                                                <Label className='!my-0'>{item.value}</Label>
                                                            </FormItem>
                                                        ))}
                                                    </RadioGroup>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <div className='flex gap-4 items-center border border-[#F2F2F2] px-6 py-3 rounded-sm'>
                                    <FormField
                                        control={form.control}
                                        name='robotFollow'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl className=''>
                                                    <RadioGroup
                                                        key={field.value}
                                                        className='flex items-center gap-24'
                                                        onValueChange={(value) => {
                                                            field.onChange(value);
                                                        }}
                                                        value={field.value}
                                                    >
                                                        {ROBOT_CHECKBOX.FOLLOW.map((item, index) => (
                                                            <FormItem key={index} className='flex items-center gap-2'>
                                                                <FormControl>
                                                                    <RadioGroupItem value={item.key} />
                                                                </FormControl>
                                                                <Label className='!my-0'>{item.value}</Label>
                                                            </FormItem>
                                                        ))}
                                                    </RadioGroup>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Open Graph Tags */}
                    <div className='bg-white p-5 rounded mt-2'>
                        <div className='text-lg font-bold text-gray-800 mb-4 border-b border-b-[#E8E8E8] pb-2'>
                            {t('words_title.open_graph_box_header')}
                        </div>
                        <div className='mb-4'>
                            <Label className='block text-sm font-medium text-gray-600 mb-1'>
                                {t('words_title.open_graph_box_title')}
                            </Label>
                            <FormField
                                control={form.control}
                                name='OGTitle'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input type='text' {...field} value={field.value ?? ''} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className=''>
                            <Label className='block text-sm font-medium text-gray-600 mb-1'>
                                {t('words_title.open_graph_box_description')}
                            </Label>
                            <FormField
                                control={form.control}
                                name='OGDescription'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Textarea
                                                maxLength={1000}
                                                className='text-input-1 min-h-[200px]'
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='mt-4'>
                            <Label className='block text-sm font-medium text-gray-600 mb-2'>
                                {t('words_title.open_graph_box_image')}
                            </Label>
                            <div className='flex flex-wrap gap-6 '>
                                <ImageInput form={form} setPreviewImage={setPreviewImage} previewImage={previewImage} />
                            </div>
                        </div>
                    </div>

                    {/* URL Settings */}
                    {seoEntityType !== SEO_ENTITY_TYPE.STATIC && (
                        <div className='bg-white p-5 rounded mt-2'>
                            <div className='text-lg font-bold text-gray-800 mb-4 border-b border-b-[#E8E8E8] pb-2'>
                                {t('words_title.url_box_header')}
                            </div>
                            <div className='mb-4'>
                                <Label className='block text-sm font-medium text-gray-600 mb-1'>
                                    {t('words_title.url_box_slug')}
                                </Label>
                                <FormField
                                    control={form.control}
                                    name='slug'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <div className='flex items-center gap-2'>
                                                    <p className='text-gray-500 whitespace-nowrap'>{getPrefixUrl()}</p>
                                                    <Input type='text' {...field} value={field.value ?? ''} />
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className=''>
                                <Label className='block text-sm font-medium text-gray-600 mb-1'>
                                    {t('words_title.url_box_canonical')}
                                </Label>
                                <FormField
                                    control={form.control}
                                    name='slug'
                                    // name='canonicalUrl'
                                    // disabled={seoEntityType === SEO_ENTITY_TYPE.STATIC}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <div className='flex items-center gap-2'>
                                                    <span className='text-gray-500 whitespace-nowrap'>
                                                        {getPrefixUrl()}
                                                    </span>
                                                    <Input type='text' readOnly {...field} value={field.value ?? ''} />
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                    )}

                    {/* Alt Settings */}
                    {(seoEntityType !== SEO_ENTITY_TYPE.STATIC && seoEntityType !== SEO_ENTITY_TYPE.SHOP) && (
                        <div className='bg-white p-5 rounded mt-2 '>
                            <div className='text-lg font-bold text-gray-800 mb-4 border-b border-b-[#E8E8E8] pb-2'>
                                {t('words_title.alt_box_header')}
                            </div>

                            <div className='grid grid-cols-[150px_1fr] bg-slate-100 border-b text-sm font-medium text-slate-600'>
                                <div className='p-2'>{t('words_title.image')}</div>
                                <div className='p-2 pl-6'>{t('words_title.alt_text')}</div>
                            </div>

                            <ScrollArea className=''>

                                <FormField
                                    control={form.control}
                                    name='listImgSEO'
                                    render={({ field, fieldState }) => {
                                        const list: ImgSEO[] = (field.value || []).map(item => ({
                                            src: item.src || '',
                                            alt: item.alt || ''
                                        }));

                                        return (
                                            <FormItem className='flex flex-col w-full'>
                                                <FormControl>
                                                    <div className='flex flex-col gap-2'>
                                                        {list.map((img, index) => (
                                                            <div
                                                                key={index}
                                                                className='grid grid-cols-[150px_1fr] text-sm items-center h-full p-2 pr-0'
                                                            >
                                                                {/* Ảnh */}
                                                                <div className='p-2 flex justify-center'>
                                                                    <Image
                                                                        src={img.src}
                                                                        alt={img.alt}
                                                                        className='h-24 rounded'
                                                                    />
                                                                </div>

                                                                {/* Cột alt */}
                                                                <div className='p-2 pl-4 pr-0'>
                                                                    <Input
                                                                        className='w-full'
                                                                        type='text'
                                                                        // placeholder={t('placeholder.alt')}
                                                                        value={img.alt}
                                                                        onChange={(e) => {
                                                                            const newList = [...list];
                                                                            newList[index].alt = e.target.value;
                                                                            field.onChange(newList);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {list.length === 0 && (
                                                            <div className='text-gray-500 w-full italic p-4 text-center'>
                                                                {t('words_title.table_seo_img_empty')}
                                                            </div>
                                                        )}
                                                    </div>
                                                </FormControl>
                                                <FormMessage>{fieldState?.error?.message}</FormMessage>
                                            </FormItem>
                                        );
                                    }}
                                />

                            </ScrollArea>

                        </div>
                    )}

                    {/* Link Settings */}
                    {(seoEntityType !== SEO_ENTITY_TYPE.STATIC && seoEntityType !== SEO_ENTITY_TYPE.SHOP) && (
                        <div className='bg-white p-5 rounded mt-2 relative'>
                            <div className='text-lg font-bold text-gray-800 mb-4 border-b border-b-[#E8E8E8] pb-2'>
                                {t('words_title.link_box_header')}
                            </div>

                            <div className='grid grid-cols-[150px_1fr] bg-slate-100 border-b text-sm font-medium text-slate-600'>
                                <div className='p-2'>{t('words_title.link')}</div>
                                <div className='p-2 pl-6'>{t('words_title.link_attributes')}</div>
                            </div>

                            <div className='overflow-x-visible mt-1'>
                                <FormField
                                    control={form.control}
                                    name='listLinkSEO'
                                    render={({ field, fieldState }) => {
                                        const list = field.value || [];

                                        return (
                                            <div className='flex flex-col gap-2 relative overflow-visible w-full'>

                                                {list.map((link, index) => (
                                                    <div
                                                        key={index}
                                                        className='grid grid-cols-[150px_1fr] items-center gap-2 relative p-2 pr-0'
                                                    >
                                                        {/* Link hiển thị */}
                                                        <a
                                                            href={link.href}
                                                            target={link.target}
                                                            rel={link.rel}
                                                            className='text-blue-500 underline p-2'
                                                        >
                                                            {link.text ?? link.href}
                                                        </a>

                                                        {/* MultiSelect */}
                                                        <MultiSelect
                                                            options={SEO_LINK_ATTRIBUTES.map(attr => ({
                                                                label: attr.label,
                                                                value: attr.key
                                                            }))}

                                                            value={(() => {
                                                                const selectedKeys = SEO_LINK_ATTRIBUTES
                                                                    .filter(attr =>
                                                                        attr.type === 'rel' ?
                                                                            link.rel?.includes(attr.value) :
                                                                            link.target === attr.value)
                                                                    .map(attr => attr.key);
                                                                return selectedKeys;
                                                            })()}
                                                            onChange={(selectedKeys) => {
                                                                const newList = [...list];
                                                                const selectedAttrs = selectedKeys.map(key =>
                                                                    SEO_LINK_ATTRIBUTES.find(a => a.key === key));

                                                                const newTarget = selectedAttrs.find(a => a?.type === 'target')?.value || '';
                                                                const newRel = selectedAttrs
                                                                    .filter(a => a?.type === 'rel')
                                                                    .map(a => a!.value)
                                                                    .join(' ');

                                                                newList[index] = {
                                                                    ...newList[index],
                                                                    target: newTarget,
                                                                    rel: newRel
                                                                };
                                                                field.onChange(newList);
                                                            }}
                                                            placeholder={t('placeholder.select_attributes')}
                                                            fieldState={fieldState}
                                                            className='w-full pl-2 z-9999'

                                                        />
                                                    </div>
                                                ))}
                                                {list.length === 0 && (
                                                    <div className='text-gray-500 w-full italic p-4 text-center'>
                                                        {t('words_title.table_seo_link_empty')}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div className='flex justify-center items-center py-6 space-x-4 '>
                        {seoEntityType !== SEO_ENTITY_TYPE.STATIC && (
                            <Button
                                type='button'
                                variant='clear'
                                className='mt-6 min-w-60'
                                onClick={() => {
                                    back();
                                }}
                            >
                                {t('button.cancel')}
                            </Button>
                        )}

                        <Button type='submit' variant='submit' className='mt-6 min-w-60'>
                            {t('button.save')}
                        </Button>
                    </div>
                </form>
            </Form>
        </div>
    );
});
